export const ERROR_MESSAGES = {
  // Credit-related errors
  INSUFFICIENT_CREDITS: {
    title: "Insufficient Credits",
    message: "You don't have enough credits for this operation. Please upgrade your plan to continue.",
    action: "Upgrade Plan",
    actionUrl: "https://app.finalpiece.ai/billing"
  },

  // Authentication errors
  AUTHENTICATION_FAILED: {
    title: "Authentication Failed",
    message: "Please sign in again to continue.",
    action: "Sign In"
  },

  // Rate limiting errors
  RATE_LIMIT_EXCEEDED: {
    title: "Rate Limit Exceeded",
    message: "You're making requests too quickly. Please wait a moment before trying again.",
    action: "Try Again"
  },

  // Network/API errors
  NETWORK_ERROR: {
    title: "Connection Error",
    message: "Unable to connect to our servers. Please check your internet connection and try again.",
    action: "Retry"
  },

  API_ERROR: {
    title: "Service Error",
    message: "Something went wrong on our end. Please try again in a moment.",
    action: "Try Again"
  },

  TIMEOUT_ERROR: {
    title: "Request Timeout", 
    message: "This operation is taking longer than expected. Please try again.",
    action: "Retry"
  },

  // AI-specific errors
  AI_GENERATION_FAILED: {
    title: "Generation Failed",
    message: "Unable to generate content. Please try again with a different prompt.",
    action: "Try Again"
  },

  THREAD_NOT_FOUND: {
    title: "Conversation Not Found",
    message: "This conversation could not be found. Please start a new one.",
    action: "New Conversation"
  },

  // File/Upload errors
  FILE_TOO_LARGE: {
    title: "File Too Large",
    message: "The selected file is too large. Please choose a smaller file.",
    action: "Choose Different File"
  },

  UNSUPPORTED_FILE_TYPE: {
    title: "Unsupported File Type",
    message: "This file type is not supported. Please choose a different file.",
    action: "Choose Different File"
  },

  // Validation errors
  INVALID_INPUT: {
    title: "Invalid Input",
    message: "Please check your input and try again.",
    action: "Fix Input"
  },

  REQUIRED_FIELD_MISSING: {
    title: "Required Field Missing",
    message: "Please fill in all required fields.",
    action: "Complete Form"
  },

  // Generic fallback
  UNKNOWN_ERROR: {
    title: "Something Went Wrong",
    message: "An unexpected error occurred. Please try again or contact support if the problem persists.",
    action: "Try Again"
  }
} as const;

export type ErrorType = keyof typeof ERROR_MESSAGES;

// Helper function to get error message with dynamic data
export const getErrorMessage = (
  errorType: ErrorType, 
  data?: Record<string, any>
) => {
  const errorConfig = ERROR_MESSAGES[errorType] || ERROR_MESSAGES.UNKNOWN_ERROR;
  
  // For insufficient credits, customize message with specific credit amounts
  if (errorType === 'INSUFFICIENT_CREDITS' && data?.creditCheck) {
    const { available_credits, required_credits } = data.creditCheck;
    return {
      ...errorConfig,
      message: `You need ${required_credits} credits but only have ${available_credits} available. Please upgrade your plan to continue.`
    };
  }
  
  return errorConfig;
}; 